import type {FC, ForwardedRef, ReactNode, Ref} from 'react';
import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import classNames from 'classnames';

import {showTooltip} from '@pb/kit-util-show-tooltip';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';


export type Props = {
    /**
     * When `true` (default), applies CSS `overflow: hidden; text-overflow:
     * ellipsis` to the element and activates the `ResizeObserver` that adds or
     * removes the tooltip as the container resizes. When `false`, the children
     * are rendered without any truncation or tooltip — useful for conditionally
     * disabling ellipsis without unmounting the component.
     */
    isActive?: boolean,

    /**
     * Override text for the native browser tooltip shown when the content is
     * clipped. When omitted, the tooltip text is derived automatically from the
     * element's `textContent`.
     */
    title?: string,

    /**
     * Content to render inside the element. Should be text or inline JSX that
     * can be meaningfully truncated in a single line.
     */
    children: ReactNode,

    /**
     * Additional CSS class names applied to the wrapper element alongside the
     * `ellipsis` utility class.
     */
    mix?: string,

    /**
     * When `true`, renders the content in a `<strong>` element instead of the
     * default `<span>`, applying bold styling via the native HTML element
     * semantics.
     */
    bold?: boolean,

    /**
     * Callback invoked whenever the trimmed state changes (i.e. when text
     * transitions between being clipped and not clipped). Receives `true` when
     * the content is currently truncated, `false` when it fits entirely. Useful
     * for conditionally rendering a "show more" control.
     */
    checkIfTextIsTrimmed?: (trimmed: boolean) => void,
};

type EllipsisRef = {
    el: HTMLElement | null,
};

interface PropsWithRef extends Props {
    ref?: Ref<EllipsisRef>,
}

/**
 * Single-line text truncation component that adds a native tooltip when the
 * content overflows its container.
 *
 * Renders the children in a `<span>` (or `<strong>` when `bold` is `true`).
 * A `ResizeObserver` tracks the element's width so the tooltip is added or
 * removed dynamically as the container resizes. The `title` attribute is set
 * via `showTooltip()` — which compares `offsetWidth` to `scrollWidth` to
 * determine whether the text is actually clipped.
 *
 * Pass `checkIfTextIsTrimmed` to receive a callback whenever the trimmed state
 * changes. Use `isActive={false}` to disable truncation and tooltip logic
 * entirely without unmounting the component.
 *
 * The forwarded ref exposes `{ el }` — a direct reference to the underlying
 * DOM element — for cases where the parent needs to measure or interact with it.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/story/text-ellipsis-text-hint}
 */
export const EllipsisTextHint = forwardRef((props: Props, ref: ForwardedRef<EllipsisRef>) => {
    const {
        mix,
        bold,
        isActive = true,
        title,
        children,
        checkIfTextIsTrimmed,
    } = props;

    const [trimmed, setTrimmed] = useState(false);

    const el = useRef<HTMLElement | null>(null);
    const observer = useRef<ResizeObserver | null>(null);

    useImperativeHandle(ref, () => ({
        el: el.current,
    }), [el.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const removeTitleObserver = () => {
        observer.current?.disconnect();
    };

    const setTitle = useCallbackWithRef(() => {
        if (isActive) {
            const newTrimmed = showTooltip(el.current, title);

            if (trimmed !== newTrimmed) {
                setTrimmed(newTrimmed);
            }
        }
    });

    useEffect(() => {
        setTitle();
    }, [bold, children, setTitle, title]);

    useEffect(() => {
        if (checkIfTextIsTrimmed) {
            checkIfTextIsTrimmed(trimmed);
        }
    }, [trimmed]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isActive && el.current) {
            observer.current = new ResizeObserver(setTitle);
            observer.current.observe(el.current);

            return removeTitleObserver;
        }
        return undefined;
    }, [isActive, setTitle, title]);

    const Tag = bold ? 'strong' : 'span';
    const classes = classNames(mix, {
        ellipsis: isActive,
    });
    return (
        <Tag className={classes} ref={el}>
            {children}
        </Tag>
    );
}) as FC<PropsWithRef>;

EllipsisTextHint.displayName = 'EllipsisTextHint';
